import React, { useState } from "react";
import { Spin, Button, Tabs } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import DocumentReviewItem from "./DocumentReviewItem";
import { useGetCustomerDocumentsForAdmin } from "../hooks/useGetCustomerDocumentsForAdmin";
import { useGetCustomerPeopleDocumentsForAdmin } from "../hooks/useGetCustomerPeopleDocumentsForAdmin";
import { ICorporateCustomer } from "../hooks/useCorporateCustomers";

import styles from "./ViewCorporateCustomer.module.scss";
import { BusinessTypes } from "../../Interfaces";

interface IViewCorporateCustomerContent {
  customerData: ICorporateCustomer;
  onClose: () => void;
}

// Match API response casing (Title Case)
enum DocStatus {
  PENDING = "Pending",
  DECLINED = "Declined",
  APPROVED = "Approved",
  REJECTED = "Rejected",
}

const ViewCorporateCustomerContent: React.FC<IViewCorporateCustomerContent> = ({
  customerData,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState("1");

  const {
    data: customerKycDocuments,
    isLoading: loadingKycDocs,
    refetch: refetchKycDocs,
  } = useGetCustomerDocumentsForAdmin({
    customerId: customerData?.customer_id,
  });

  const {
    data: customerPeopleKycDocuments,
    isLoading: loadingPeopleDocs,
    refetch: refetchPeopleDocs,
  } = useGetCustomerPeopleDocumentsForAdmin({
    customerId: customerData?.customer_id,
  });

  const refetchAllDocuments = () => {
    refetchKycDocs();
    refetchPeopleDocs();
  };

  const isLoading = loadingKycDocs || loadingPeopleDocs;

  // Transform company documents to match DocumentReviewItem expected format
  const rawCompanyDocs = customerKycDocuments?.data || [];
  const companyDocuments = rawCompanyDocs.map((doc: any) => {
    const customerDoc = doc.customer_documents?.[0];
    const latestFile = customerDoc?.files?.[customerDoc.files.length - 1];

    return {
      name: doc.document_field_Name,
      documentType: doc.document_field_Name,
      documentTypeCode: doc.document_field_code,
      documentFieldId: doc.document_field_id,
      files:
        customerDoc?.files?.map((file: any) => ({
          id: file.customer_document_id,
          fileName: file.file_name,
          fileUrl: file.upload_path,
          status: file.document_status,
          uploadedDate: file.uploaded_at,
          comments: file.comments || [],
          documentFieldId: doc.document_field_id,
        })) || [],
    };
  });

  const peopleData = customerPeopleKycDocuments?.data.funderPeople || [];

  // Flatten funderPeopleDetails from each funderPeople group
  const peopleDocuments = peopleData.flatMap(
    (group: any) => group.funderPeopleDetails || [],
  );

  // Filter to show only Directors, Partners, Trustees (not Senior Management)
  const filteredPeopleDocuments = peopleDocuments.filter(
    (person: any) => person.funderPeopleType !== "SeniorManagement",
  );

  const allDocsApproved =
    companyDocuments.every((doc: any) =>
      doc.files?.every((file: any) => file.status === DocStatus.APPROVED),
    ) &&
    filteredPeopleDocuments.every((person: any) => {
      return (
        person.proofOfAddressApprovalStatus === DocStatus.APPROVED &&
        person.governmentIdApprovalStatus === DocStatus.APPROVED
      );
    });

  const hasPendingOrDeclined =
    companyDocuments.some((doc: any) =>
      doc.files?.some(
        (file: any) =>
          file.status === DocStatus.PENDING ||
          file.status === DocStatus.DECLINED ||
          file.status === DocStatus.REJECTED,
      ),
    ) ||
    filteredPeopleDocuments.some((person: any) => {
      const proofStatus = person.proofOfAddressApprovalStatus;
      const govIdStatus = person.governmentIdApprovalStatus;
      return (
        proofStatus === DocStatus.PENDING ||
        proofStatus === DocStatus.DECLINED ||
        proofStatus === DocStatus.REJECTED ||
        govIdStatus === DocStatus.PENDING ||
        govIdStatus === DocStatus.DECLINED ||
        govIdStatus === DocStatus.REJECTED
      );
    });

  const getStakeHolderTitle = (businessType: BusinessTypes) => {
    switch (businessType) {
      case BusinessTypes.LIMITED_LIABILITY:
        return "Directors";

      case BusinessTypes.REGISTERED_BUSINESS:
        return "Business Owner";

      case BusinessTypes.PARTNERSHIP:
        return "Partners";

      case BusinessTypes.NGO:
        return "Registered Trustees";

      default:
        return "Stakeholders";
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
        <p>Loading customer documents...</p>
      </div>
    );
  }

  const tabItems = [
    {
      key: "1",
      label: "Company Documents",
      children: (
        <div className={styles.documentList}>
          {companyDocuments.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No company documents uploaded yet</p>
            </div>
          ) : (
            companyDocuments.map((document: any, index: number) => (
              <DocumentReviewItem
                key={index}
                document={document}
                customerId={customerData?.customer_id}
                onDocumentUpdate={refetchAllDocuments}
              />
            ))
          )}
        </div>
      ),
    },
    {
      key: "2",
      label: `${getStakeHolderTitle(customerData?.business_subtype_id)} (${filteredPeopleDocuments.length})`,
      children: (
        <div className={styles.documentList}>
          {filteredPeopleDocuments.length === 0 ? (
            <div className={styles.emptyState}>
              <p>
                No {getStakeHolderTitle(customerData?.business_subtype_id)}{" "}
                uploaded yet
              </p>
            </div>
          ) : (
            filteredPeopleDocuments.map((person: any, personIndex: number) => (
              <div key={personIndex} className={styles.personSection}>
                <h3 className={styles.personName}>
                  {person.firstName} {person.lastName}
                  <span className={styles.personRole}>
                    ({person.funderPeopleType})
                  </span>
                </h3>
                <div className={styles.personDocuments}>
                  {/* Proof of Address Document */}
                  {person.proofOfAddressUploaded && (
                    <DocumentReviewItem
                      key={`${personIndex}-poa`}
                      document={{
                        name: "Proof of Address",
                        documentType: "Proof of Address",
                        documentTypeId: 2,
                        documentTypeCode:
                          person.proofOfAddressDocumentTypeCode || "POA",
                        files: [
                          {
                            // id: `poa-${person.funderPeopleId}`,
                            fileName: person.proofOfAddressFileName,
                            fileUrl: person.proofOfAddressFilePath,
                            status:
                              person.proofOfAddressApprovalStatus || "Pending",
                            uploadedDate: person.dateCreated,
                            comments: person.proofOfAddressComment
                              ? [{ comment: person.proofOfAddressComment }]
                              : [],
                          },
                        ],
                      }}
                      customerId={customerData?.customer_id}
                      personId={person.funderPeopleId}
                      onDocumentUpdate={refetchAllDocuments}
                    />
                  )}
                  {/* Government ID Document */}
                  {person.governmentIdUploaded && (
                    <DocumentReviewItem
                      key={`${personIndex}-govid`}
                      document={{
                        name: "Government Issued ID",
                        documentType: `Government Issued ID`,
                        documentTypeId: 1,
                        governmentIdType: person.governmentIdType,
                        documentTypeCode:
                          person.governmentIdDocumentTypeCode || "GOVID",
                        files: [
                          {
                            id: `govid-${person.funderPeopleId}`,
                            fileName: person.governmentIdFileName,
                            fileUrl: person.governmentIdFilePath,
                            status:
                              person.governmentIdApprovalStatus || "Pending",
                            uploadedDate: person.dateCreated,
                            comments: person.governmentIdComment
                              ? [{ comment: person.governmentIdComment }]
                              : [],
                          },
                        ],
                      }}
                      customerId={customerData?.customer_id}
                      personId={person.funderPeopleId}
                      onDocumentUpdate={refetchAllDocuments}
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.viewCorporateCustomerContainer}>
      <div className={styles.header}>
        <h1>Document Review Progress</h1>
        <div className={styles.businessInfo}>
          <p>
            <strong>{customerData?.biz_name}</strong>
          </p>
          <p>RC Number: {customerData?.biz_registration_number || "N/A"}</p>
          <p>Business Type: {customerData?.business_subtype || "N/A"}</p>
          <p>Email: {customerData?.biz_email || "N/A"}</p>
        </div>
        {allDocsApproved && (
          <div
            className={styles.statusBanner}
            style={{ background: "#EAF9EA" }}
          >
            <CheckCircleOutlined style={{ color: "#25AD25" }} />
            <span>All documents have been approved - Onboarding complete!</span>
          </div>
        )}
        {hasPendingOrDeclined && (
          <div
            className={styles.statusBanner}
            style={{ background: "#FEF4E5" }}
          >
            <CloseCircleOutlined style={{ color: "#F5A623" }} />
            <span>
              Documents are currently being reviewed. Check back for updates or
              reupload rejected documents.
            </span>
          </div>
        )}
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        className={styles.documentTabs}
      />

      <div className={styles.footer}>
        <Button onClick={onClose} size="large" style={{ minWidth: 200 }}>
          Close
        </Button>
      </div>
    </div>
  );
};

export default ViewCorporateCustomerContent;
